import { App, Button, Drawer, Flex, Form, Select, Tag, Typography } from 'antd';
import { SendOutlined, SoundOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FixedTextArea } from '@/shared/ui/primitives';
import { TAGS } from '@/shared/query/keys';
import { useMutation } from '@/shared/query/useMutation';
import { ERROR_MESSAGE_KEY } from '@/shared/api/errors';
import { broadcastNotice, type NotificationImportance } from './api';

/**
 * 公告抽屉——**admin 发公告**（后端 `POST /api/v1/notifications/broadcast`）。
 *
 * ## 为什么是独立抽屉而不是通知中心里的一个按钮
 *
 * 公告是**平台级写动作**，与「看通知」是读面，两件事的生命周期与失败语义都不同
 * （发公告 5xx 必须有人看见，抽屉不动 + 无提示会让人以为发成功了）⇒ 挂壳里
 * 当独立浮层（见 `layouts/AppLayout.tsx`）。
 *
 * ## 收件人口径
 *
 * 本抽屉只发**全员广播**（不给 `recipient_user_ids` ⇒ 后端按「共享行 + 每用户一个
 * `last_read_at` 水位」落行，不逐用户落行）。定向广播后端已支持（逐收件人各一行），
 * 但需要**用户选择器**才能接线 ⇒ 前端入口先只画全员。
 *
 * ## 打断强度
 *
 * `NORMAL` / `IMPORTANT` 两档大写，与后端 DB CHECK 逐字相等 ⇒ 这里是字面量，
 * 不走后端词表（打断强度不是业务状态，是**档位常量**）。
 */

interface BroadcastForm {
  importance: NotificationImportance;
  message: string;
}

/**
 * 公告正文上限——与后端 `MAX_BROADCAST_MESSAGE_LEN`（2000）对齐。
 *
 * ⚠️ 后端会拒超长正文（422）⇒ 前端同限只是**早失败**，不是唯一防线。
 */
const MESSAGE_MAX = 2000;

export function BroadcastDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<BroadcastForm>();
  const publish = useMutation(broadcastNotice, {
    invalidate: [TAGS.notifications],
    onSuccess: () => {
      message.success(t('notifications.broadcast.published'));
      form.resetFields();
      onClose();
    },
    // 必须有 onError：否则公告 5xx 完全静默（抽屉不动、无提示，用户以为发成功了）。
    onError: (error) => message.error(t(ERROR_MESSAGE_KEY[error.code]), 6),
  });
  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <SoundOutlined />
          <span>{t('notifications.broadcast.title')}</span>
        </Flex>
      }
      open={open}
      onClose={onClose}
      size={520}
    >
      <Flex vertical gap="middle">
        <Form<BroadcastForm>
          form={form}
          layout="vertical"
          initialValues={{ importance: 'NORMAL' }}
          onFinish={(values) => {
            publish.fire({ importance: values.importance, message: values.message.trim() });
          }}
        >
          <Form.Item
            name="importance"
            label={t('notifications.broadcast.importance')}
            rules={[{ required: true }]}
          >
            <Select<NotificationImportance>
              options={[
                {
                  value: 'NORMAL',
                  label: (
                    <Flex align="center" gap={8}>
                      <Tag>{t('notifications.broadcast.importanceNormal')}</Tag>
                    </Flex>
                  ),
                },
                {
                  value: 'IMPORTANT',
                  label: (
                    <Flex align="center" gap={8}>
                      <Tag color="red">{t('notifications.broadcast.importanceImportant')}</Tag>
                    </Flex>
                  ),
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="message"
            label={t('notifications.broadcast.message')}
            rules={[
              {
                required: true,
                // ⚠️ 必须显式给文案：antd 的 `required` 默认消息是英文的
                // 「'message' is required」（拿字段名拼的）⇒ 中文界面里冒英文。
                message: t('notifications.broadcast.messageRequired'),
                max: MESSAGE_MAX,
              },
            ]}
          >
            <FixedTextArea rows={5} maxLength={MESSAGE_MAX} showCount />
          </Form.Item>
          <Flex vertical gap={4}>
            <Typography.Text type="secondary">
              {t('notifications.broadcast.target')}: {t('notifications.broadcast.targetAll')}
            </Typography.Text>
          </Flex>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={publish.loading}
            icon={<SendOutlined />}
          >
            {t('notifications.broadcast.publish')}
          </Button>
        </Form>
      </Flex>
    </Drawer>
  );
}
